import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

import {
  BaseComponent,
  BooleanStrings,
  FormatService,
  ModalService,
  OkModalComponent,
  ReportTableColumn,
  SummaryItem,
} from '../../../../../tms-shared';
import {
  PickingListAllocationsReport,
  PickingListReport,
  PickingListReportLine,
  PickingListReportWarehouse,
  SalesOrderExtraReport,
} from '../../../../models';
import { SalesOrderReportsService } from '../../../../services/edit-create/sales/sales-order-reports.service';
import { SalesOrderService } from '../../../../services/sales/sales-order.service';

@Component({
  selector: 'app-picking-list',
  templateUrl: './picking-list.component.html',
  styleUrl: './picking-list.component.scss',
})
export class PickingListComponent extends BaseComponent implements OnInit {
  @Input() salesOrderHeaderId: string;
  @Input() hideReport = false;

  reportModel: PickingListReport;

  constructor(
    private salesOrderReportsService: SalesOrderReportsService,
    private salesOrderService: SalesOrderService,
    private formatService: FormatService,
    private modalService: ModalService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.salesOrderHeaderId) {
      this.salesOrderHeaderId = this.route.snapshot.paramMap.get('id');
    }
    this.getModel();

    this.salesOrderService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.getModel());
  }

  private getModel() {
    this.salesOrderReportsService
      .getPickingList(this.salesOrderHeaderId, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.reportModel = model;
        setTimeout(() => {
          if (!this.hideReport) {
            if (this.reportModel.alreadyPrinted) {
              const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
              component.header = $localize`Picking List Print`;
              component.message = $localize`Warning - Picking List already printed`;
              modal.result.then(
                () => {},
                () => {},
              );
            }
          } else {
            window.print();
            this.modalService.close();
          }
        }, 0);
      });
  }

  pickingListSummaryItems(model: PickingListReport): SummaryItem[] {
    return [
      { label: $localize`Order Number`, value: model.orderNo },
      { label: $localize`Your Order Number`, value: model.customerOrderNo },
      { label: $localize`Your Order Reference`, value: model.customerOrderRef },
      { label: $localize`Shipper`, value: model.shipper },
      { label: $localize`Customer Account`, value: model.customerKey },
      { label: $localize`Order Date`, value: this.formatService.date(model.dateOrdered) },
      { label: $localize`Entered By`, value: model.enteredBy },
    ];
  }

  lineColumnsWarehouse(model: PickingListReportWarehouse): ReportTableColumn[] {
    return [
      {
        label: $localize`Warehouse`,
        value: model.warehouse,
      },
    ];
  }

  lineColumns(model: PickingListReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productKey,
      },
      {
        label: $localize`Description`,
        value: this.formatService.valuesToHtml(this.formatProductDescription(model)),
      },
      {
        label: $localize`Despatch Date`,
        value: this.formatService.date(model.deliveryDate),
        textRight: true,
      },
      {
        label: $localize`Order Quantity`,
        value: `${this.formatService.number(model.quantity)} ${model.unit}`,
        textRight: true,
      },
    ];
  }

  lineSubTableColumnsWarehouse(model: PickingListReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productKey,
      },
      {
        label: $localize`Description`,
        value: model.productDescription,
      },
      {
        label: $localize`Location`,
        value: model.location,
      },
      {
        label: $localize`Despatch Date`,
        value: model.deliveryDateString,
      },
      {
        label: $localize`Order Quantity`,
        value: `${model.quantity} ${model.unit}`,
        textRight: true,
      },
    ];
  }

  lineSubTableColumns(model: PickingListAllocationsReport): ReportTableColumn[] {
    return [
      { label: $localize`Stock Number`, value: model.stockNo },
      {
        label: model.remeasureBeforeCutting ? $localize`Re-Measure Before Cutting` : $localize`Re-Measure`,
        value: model.reMeasure ? BooleanStrings.yes : BooleanStrings.no,
      },
      { label: $localize`Batch`, value: model.batch },
      { label: $localize`Warehouse`, value: model.warehouse },
      { label: $localize`Location`, value: model.location },
      {
        label: $localize`Balance`,
        value: `${model.balance} ${model.unit}`,
        textRight: true,
      },
      {
        label: $localize`Allocated`,
        value: `${model.allocated} ${model.unit}`,
        textRight: true,
      },
      { label: $localize`Picked`, value: `${model.picked} ${model.unit}`, textRight: true },
    ];
  }

  extraColumns(model: SalesOrderExtraReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  footerSummaryItems(model: PickingListReport): SummaryItem[] {
    return [
      {
        label: model.internalNotes ? $localize`Notes` : '',
        value: this.formatService.multiLineToHtml(model.internalNotes),
      },
      { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
      { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
      { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
      { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
      { label: model.shippingNotes ? $localize`ShippingNotes` : '', value: model.shippingNotes },
      { label: model.documentNotes ? $localize`Document Notes` : '', value: model.documentNotes },
      { label: model.deliveryInstructions ? $localize`Delivery Instructions` : '', value: model.deliveryInstructions },
      {
        label: model.customerNotes ? $localize`Customer Notes` : '',
        value: this.formatService.multiLineToHtml(model.customerNotes),
      },
    ];
  }

  private formatProductDescription(model: PickingListReportLine): string[] {
    const values = [model.productDescription];
    if (model.productOldCode && model.productOldCode !== model.productKey) {
      values.push($localize`Alternate Code` + ' ' + model.productOldCode);
    }
    if (model.customerProduct) {
      values.push($localize`Customer Product` + ' ' + model.customerProduct);
    }
    if (model.customerLineRef) {
      values.push($localize`Order Reference` + ' ' + model.customerLineRef);
    }
    if (model.makeUp) {
      values.push($localize`Make Up` + ' ' + model.makeUp);
    }
    return values;
  }
}
